import { useEffect, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import { Card, CardHeader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PlugsConnected, Plugs, ShieldCheck, ArrowSquareOut, Warning, ArrowsClockwise, CircleNotch } from "@phosphor-icons/react";
import SyncPreviewModal from "@/components/SyncPreviewModal";
import { toast } from "sonner";

/**
 * Broker Settings page — connect your own Alpaca paper-trading account so
 * bots with "Route to Alpaca" enabled fire real market orders in parallel
 * with the STRATOS paper ledger.
 *
 * Keys are validated at Alpaca before being encrypted (Fernet) at rest.
 */
export default function BrokerPage() {
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [apiKey, setApiKey] = useState("");
  const [apiSecret, setApiSecret] = useState("");
  const [saving, setSaving] = useState(false);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [diff, setDiff] = useState(null);
  const [diffLoading, setDiffLoading] = useState(false);
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncHistory, setSyncHistory] = useState([]);

  const load = () => {
    setLoading(true);
    api.get("/alpaca/account")
      .then((r) => setAccount(r.data))
      .catch(() => setAccount({ connected: false }))
      .finally(() => setLoading(false));
  };

  const loadOrders = () => {
    setOrdersLoading(true);
    api.get("/alpaca/orders?limit=25")
      .then((r) => setOrders(r.data.orders || []))
      .catch(() => setOrders([]))
      .finally(() => setOrdersLoading(false));
  };

  const loadDiff = () => {
    setDiffLoading(true);
    api.get("/alpaca/positions/diff")
      .then((r) => setDiff(r.data))
      .catch(() => setDiff(null))
      .finally(() => setDiffLoading(false));
  };

  const loadSyncHistory = () => {
    api.get("/alpaca/sync/history?limit=10")
      .then((r) => setSyncHistory(r.data.events || []))
      .catch(() => setSyncHistory([]));
  };

  useEffect(load, []);
  useEffect(() => {
    if (account?.connected && !account?.error) { loadOrders(); loadDiff(); loadSyncHistory(); }
    else { setOrders([]); setDiff(null); setSyncHistory([]); }
  }, [account?.connected, account?.error]);

  const connect = async () => {
    if (!apiKey.trim() || !apiSecret.trim()) {
      toast.error("Both key and secret required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/alpaca/keys", { api_key: apiKey.trim(), api_secret: apiSecret.trim() });
      toast.success("Alpaca account connected");
      setApiKey(""); setApiSecret("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Connection failed");
    } finally { setSaving(false); }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Alpaca? All bots with 'Route to Alpaca' will be reverted to paper-only.")) return;
    try {
      await api.delete("/alpaca/keys");
      toast.success("Alpaca disconnected");
      load();
    } catch { toast.error("Disconnect failed"); }
  };

  const connected = account?.connected === true;
  const hasError = connected && account?.error;

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="broker-page">
      <div>
        <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest">
          Terminal / Broker
        </div>
        <h1 className="heading text-4xl font-black tracking-tighter mt-2">Live broker</h1>
        <div className="mono text-sm text-zinc-400 mt-3 max-w-2xl">
          Connect your own Alpaca <span className="text-white">paper-trading</span> account.
          When enabled on a bot, every STRATOS trade is mirrored as a real market
          order to Alpaca. Paper trading remains the source of truth for the UI
          and leaderboard.
        </div>
      </div>

      <Card>
        <CardHeader
          title="Alpaca connection"
          action={
            <span
              className={`mono text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm border ${
                connected && !hasError
                  ? "border-[#00C805] text-[#00C805]"
                  : connected && hasError
                  ? "border-[#FFA500] text-[#FFA500]"
                  : "border-zinc-700 text-zinc-500"
              }`}
              data-testid="alpaca-status-chip"
            >
              {connected ? (hasError ? "Error" : "Connected") : "Not connected"}
            </span>
          }
        />

        {loading ? (
          <div className="p-6 mono text-xs text-zinc-500">Loading…</div>
        ) : connected && !hasError ? (
          <div className="p-6 space-y-4" data-testid="alpaca-connected-panel">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Kpi label="Account" value={account.account_number || "—"} />
              <Kpi label="Status" value={account.status || "—"} />
              <Kpi label="Buying power" value={fmtMoney(account.buying_power || 0)} />
              <Kpi label="Cash" value={fmtMoney(account.cash || 0)} />
            </div>
            <div className="mono text-xs text-zinc-500">
              Connected {account.connected_at ? new Date(account.connected_at).toLocaleString() : ""}
            </div>
            {account.trading_blocked && (
              <div className="p-3 rounded-sm border border-[#FFA500] text-[#FFA500] mono text-xs flex items-center gap-2">
                <Warning size={14} weight="bold" />
                Alpaca reports this account is trading-blocked. Fix in the Alpaca dashboard before enabling bots.
              </div>
            )}
            <div className="flex gap-2">
              <Button
                onClick={disconnect}
                variant="outline"
                className="border-zinc-700 bg-transparent hover:bg-zinc-900 rounded-sm mono text-[10px] uppercase tracking-widest gap-2"
                data-testid="alpaca-disconnect-btn"
              >
                <Plugs size={12} /> Disconnect
              </Button>
              <a
                href="https://app.alpaca.markets/paper/dashboard/overview"
                target="_blank"
                rel="noopener noreferrer"
                className="mono text-[10px] uppercase tracking-widest px-3 py-2 rounded-sm border border-zinc-700 text-zinc-400 hover:text-white hover:border-white inline-flex items-center gap-1.5"
              >
                Alpaca dashboard <ArrowSquareOut size={12} />
              </a>
            </div>
          </div>
        ) : (
          <div className="p-6 space-y-4" data-testid="alpaca-connect-panel">
            {hasError && (
              <div className="p-3 rounded-sm border border-[#FF3B30] text-[#FF3B30] mono text-xs">
                Alpaca error: {account.error}
              </div>
            )}
            <ol className="mono text-xs text-zinc-400 space-y-1.5 list-decimal list-inside">
              <li>
                Create a free Alpaca account at{" "}
                <a href="https://alpaca.markets" target="_blank" rel="noopener noreferrer"
                   className="text-white underline">alpaca.markets</a>
              </li>
              <li>
                Open the <span className="text-white">Paper Trading</span> dashboard and click
                "Generate API Keys" in the right sidebar
              </li>
              <li>Paste your key + secret below — we validate them and never store them plaintext</li>
            </ol>
            <div className="space-y-2 max-w-xl">
              <Input
                type="text"
                placeholder="API Key ID (starts with PK...)"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                className="bg-zinc-900 border-zinc-800 mono text-sm"
                data-testid="alpaca-api-key-input"
              />
              <Input
                type="password"
                placeholder="API Secret"
                value={apiSecret}
                onChange={(e) => setApiSecret(e.target.value)}
                className="bg-zinc-900 border-zinc-800 mono text-sm"
                data-testid="alpaca-api-secret-input"
              />
              <Button
                onClick={connect}
                disabled={saving}
                className="bg-white text-black hover:bg-zinc-200 rounded-sm mono text-[10px] uppercase tracking-widest gap-2 disabled:opacity-50"
                data-testid="alpaca-connect-btn"
              >
                <PlugsConnected size={12} weight="bold" /> {saving ? "Validating…" : "Connect Alpaca"}
              </Button>
            </div>
            <div className="mono text-[10px] text-zinc-600 flex items-center gap-1 pt-2">
              <ShieldCheck size={11} /> Secrets are Fernet-encrypted at rest and never returned to the client.
            </div>
          </div>
        )}
      </Card>

      {connected && !hasError && diff && (
        <Card data-testid="positions-diff-card">
          <CardHeader
            title="Paper vs live positions"
            action={
              <div className="flex items-center gap-3">
                <span className="mono text-[10px] uppercase tracking-widest">
                  <span className="text-[#00C805]">{diff.summary?.in_sync || 0} in sync</span>
                  {" · "}
                  <span className={(diff.summary?.diverged || 0) > 0 ? "text-amber-400" : "text-zinc-500"}>
                    {diff.summary?.diverged || 0} diverged
                  </span>
                </span>
                {(diff.summary?.diverged || 0) > 0 && (
                  <button
                    onClick={() => setSyncOpen(true)}
                    data-testid="positions-diff-sync"
                    className="mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm bg-white text-black hover:bg-zinc-200 inline-flex items-center gap-1"
                  >
                    <PlugsConnected size={12} weight="bold" /> Sync now
                  </button>
                )}
                <button
                  onClick={loadDiff}
                  data-testid="positions-diff-refresh"
                  className="mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white inline-flex items-center gap-1"
                >
                  {diffLoading ? <CircleNotch size={12} className="animate-spin" /> : <ArrowsClockwise size={12} />}
                  Refresh
                </button>
              </div>
            }
          />
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] mono uppercase tracking-widest text-zinc-500">
                <tr className="border-b border-zinc-800">
                  <th className="text-left px-5 py-2.5">Symbol</th>
                  <th className="text-right px-3 py-2.5">Paper qty</th>
                  <th className="text-right px-3 py-2.5">Alpaca qty</th>
                  <th className="text-right px-3 py-2.5">Delta</th>
                  <th className="text-left px-5 py-2.5">Status</th>
                </tr>
              </thead>
              <tbody className="mono">
                {diff.rows.map((r) => (
                  <tr
                    key={r.symbol}
                    className="border-b border-zinc-800/50 hover:bg-zinc-800/30"
                    data-testid={`diff-row-${r.symbol}`}
                    data-status={r.status}
                  >
                    <td className="px-5 py-3 font-semibold">{r.symbol}</td>
                    <td className="text-right px-3 py-3">{r.paper_qty}</td>
                    <td className="text-right px-3 py-3">{r.alpaca_qty}</td>
                    <td className={`text-right px-3 py-3 ${r.delta === 0 ? "text-zinc-500" : r.delta > 0 ? "profit" : "loss"}`}>
                      {r.delta > 0 ? "+" : ""}{r.delta}
                    </td>
                    <td className="px-5 py-3">
                      <span
                        className={`mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm ${
                          r.status === "in_sync"
                            ? "bg-[#00C805]/15 text-[#00C805]"
                            : r.status === "paper_only"
                            ? "bg-zinc-800 text-zinc-400"
                            : r.status === "alpaca_only"
                            ? "bg-amber-500/15 text-amber-400"
                            : "bg-[#FF3B30]/15 text-[#FF3B30]"
                        }`}
                      >
                        {r.status.replace("_", " ")}
                      </span>
                    </td>
                  </tr>
                ))}
                {diff.rows.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-5 py-12 text-center mono text-xs text-zinc-600">
                      No positions on either side yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {connected && !hasError && (
        <Card data-testid="alpaca-orders-card">
          <CardHeader
            title="Recent broker orders"
            action={
              <button
                onClick={loadOrders}
                data-testid="alpaca-orders-refresh"
                className="mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white inline-flex items-center gap-1"
              >
                {ordersLoading ? (
                  <CircleNotch size={12} className="animate-spin" />
                ) : (
                  <ArrowsClockwise size={12} />
                )}
                Refresh
              </button>
            }
          />
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] mono uppercase tracking-widest text-zinc-500">
                <tr className="border-b border-zinc-800">
                  <th className="text-left px-5 py-2.5">Submitted</th>
                  <th className="text-left px-3 py-2.5">Symbol</th>
                  <th className="text-left px-3 py-2.5">Side</th>
                  <th className="text-right px-3 py-2.5">Qty</th>
                  <th className="text-right px-3 py-2.5">Filled</th>
                  <th className="text-right px-3 py-2.5">Avg fill</th>
                  <th className="text-left px-5 py-2.5">Status</th>
                </tr>
              </thead>
              <tbody className="mono">
                {orders.map((o) => (
                  <tr
                    key={o.id}
                    className="border-b border-zinc-800/50 hover:bg-zinc-800/30"
                    data-testid={`alpaca-order-row-${o.id}`}
                  >
                    <td className="px-5 py-3 text-zinc-400 text-xs">
                      {o.submitted_at ? new Date(o.submitted_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-3 py-3 font-semibold">{o.symbol}</td>
                    <td className={`px-3 py-3 uppercase text-[10px] font-bold ${o.side === "buy" ? "profit" : "loss"}`}>
                      {o.side}
                    </td>
                    <td className="text-right px-3 py-3">{o.qty}</td>
                    <td className="text-right px-3 py-3">{o.filled_qty}</td>
                    <td className="text-right px-3 py-3">
                      {o.filled_avg_price > 0 ? `$${o.filled_avg_price.toFixed(2)}` : "—"}
                    </td>
                    <td className="px-5 py-3">
                      <span
                        className={`mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm ${
                          o.status === "filled"
                            ? "bg-[#00C805]/15 text-[#00C805]"
                            : o.status === "canceled" || o.status === "rejected" || o.status === "expired"
                            ? "bg-[#FF3B30]/15 text-[#FF3B30]"
                            : "bg-zinc-800 text-zinc-400"
                        }`}
                      >
                        {o.status || "—"}
                      </span>
                    </td>
                  </tr>
                ))}
                {orders.length === 0 && !ordersLoading && (
                  <tr>
                    <td colSpan={7} className="px-5 py-12 text-center mono text-xs text-zinc-600">
                      No orders yet — deploy a bot with the Alpaca toggle enabled to see live fills here.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <SyncPreviewModal
        open={syncOpen}
        onClose={() => setSyncOpen(false)}
        onSubmitted={() => { loadDiff(); loadSyncHistory(); }}
      />

      {connected && !hasError && syncHistory.length > 0 && (
        <Card data-testid="sync-history-card">
          <CardHeader
            title="Recent syncs"
            action={
              <div className="flex items-center gap-3">
                <span className="mono text-[10px] text-zinc-500 uppercase tracking-widest">
                  Audit trail — last {syncHistory.length}
                </span>
                <button
                  onClick={() => {
                    // Client-side CSV — no need for another endpoint.
                    const rows = [["at","planned","submitted","failed","filtered_symbols","orders"]];
                    syncHistory.forEach(e => {
                      const orders = [...(e.submitted || []), ...(e.failed || [])]
                        .map(o => `${o.side?.toUpperCase()} ${o.qty} ${o.symbol}${o.status ? ` [${o.status}]` : ""}${o.error ? ` (${o.error})` : ""}`)
                        .join(" | ");
                      rows.push([
                        e.at || "",
                        e.planned_count || 0,
                        e.submitted?.length || 0,
                        e.failed?.length || 0,
                        (e.filtered_symbols || []).join(" ") || "",
                        orders,
                      ]);
                    });
                    const csv = rows.map(r => r.map(c => {
                      const s = String(c ?? "");
                      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
                    }).join(",")).join("\n");
                    const blob = new Blob([csv], { type: "text/csv" });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `stratos_sync_history_${new Date().toISOString().slice(0,10)}.csv`;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    URL.revokeObjectURL(url);
                    toast.success("Sync history exported");
                  }}
                  data-testid="sync-history-export"
                  className="mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white inline-flex items-center gap-1"
                >
                  <ArrowsClockwise size={12} /> Export CSV
                </button>
              </div>
            }
          />
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] mono uppercase tracking-widest text-zinc-500">
                <tr className="border-b border-zinc-800">
                  <th className="text-left px-5 py-2.5">When</th>
                  <th className="text-right px-3 py-2.5">Planned</th>
                  <th className="text-right px-3 py-2.5">Submitted</th>
                  <th className="text-right px-3 py-2.5">Failed</th>
                  <th className="text-left px-5 py-2.5">Symbols</th>
                </tr>
              </thead>
              <tbody className="mono">
                {syncHistory.map((e) => (
                  <tr key={e.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30" data-testid={`sync-history-row-${e.id}`}>
                    <td className="px-5 py-3 text-zinc-400 text-xs">
                      {e.at ? new Date(e.at).toLocaleString() : "—"}
                    </td>
                    <td className="text-right px-3 py-3">{e.planned_count || 0}</td>
                    <td className="text-right px-3 py-3 profit">{e.submitted?.length || 0}</td>
                    <td className={`text-right px-3 py-3 ${(e.failed?.length || 0) > 0 ? "loss" : "text-zinc-500"}`}>
                      {e.failed?.length || 0}
                    </td>
                    <td className="px-5 py-3 text-zinc-400 text-xs truncate max-w-[280px]">
                      {[...(e.submitted || []), ...(e.failed || [])]
                        .map((o) => `${o.side?.toUpperCase()} ${o.qty} ${o.symbol}`)
                        .join(", ") || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

function Kpi({ label, value }) {
  return (
    <div className="p-3 border border-zinc-800 rounded-sm bg-zinc-900/40">
      <div className="mono text-[9px] text-zinc-500 uppercase tracking-widest">{label}</div>
      <div className="mono text-base font-bold mt-1 text-white">{value}</div>
    </div>
  );
}
